// Core
import { useEffect, useState } from 'react';
import { MdToken } from 'react-icons/md';
import { BiSolidSprayCan } from 'react-icons/bi';
import { FaCar } from 'react-icons/fa';
import { FaArrowsLeftRight, FaArrowRotateRight } from 'react-icons/fa6';

// Chakra
import {
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

// Components
import { lvls } from '../LVL';
import ResetProgressModal from '../../../components/Attentions/ResetProgress';

const AchievementsModal = ({
  tokens,
  allTokens,
  cars,
  pedals,
  colors,
  isOpen,
  onOpen,
  onClose,
}) => {
  const [objectsLVL, setObjectsLVL] = useState(lvls[0]);

  const {
    isOpen: ResetProgressIsOpen,
    onOpen: ResetProgressOnOpen,
    onClose: ResetProgressOnClose,
  } = useDisclosure();

  const [countCars, setCountCars] = useState(0);
  const [countColors, setCountColors] = useState(0);
  const [countPedals, setCountPedals] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let count = 0;

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);

      if (key && key.startsWith('myCar')) {
        count += 1;
      }
    }

    setCountCars(count);
  }, [tokens]);

  useEffect(() => {
    let count = 0;

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);

      if (key && key.startsWith('myColor')) {
        count += 1;
      }
    }

    setCountColors(count);
  }, [tokens]);

  useEffect(() => {
    let count = 0;

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);

      if (key && key.startsWith('myPedal')) {
        count += 1;
      }
    }

    setCountPedals(count);
  }, [tokens]);

  const allTokensValue = Math.min(
    100,
    (allTokens / 20000) * 100
  );

  const getClosestLevel = (levels, newProgress) => {
    return levels.reduce((closest, lvl) => {
      if (
        lvl.progress <= newProgress &&
        lvl.progress > (closest?.progress || 0)
      ) {
        return lvl;
      }

      return closest;
    }, levels[0]);
  };

  useEffect(() => {
    const newProgress =
      (
        (
          countCars +
          countColors +
          countPedals +
          allTokensValue
        ) /
        (
          cars.length +
          colors.length +
          pedals.length +
          100 -
          3 -
          3 -
          1
        )
      ) * 100;

    const safeProgress = Math.min(
      100,
      Math.max(0, newProgress)
    );

    setProgress(safeProgress);
    setObjectsLVL(
      getClosestLevel(lvls, safeProgress)
    );
  }, [
    countCars,
    countColors,
    countPedals,
    allTokensValue,
    cars.length,
    colors.length,
    pedals.length,
  ]);

  const achievements = [
    {
      id: 'cars',
      title: 'Cars',
      subtitle: 'Vehicles unlocked',
      icon: FaCar,
      color: '#C344FF',
      glow: 'rgba(195, 68, 255, 0.45)',
      value: countCars + 3,
      total: cars.length,
      progress: Math.min(
        100,
        ((countCars + 3) / cars.length) * 100
      ),
    },
    {
      id: 'colors',
      title: 'Colors',
      subtitle: 'Paints unlocked',
      icon: BiSolidSprayCan,
      color: '#39F56B',
      glow: 'rgba(57, 245, 107, 0.4)',
      value: countColors + 3,
      total: colors.length,
      progress: Math.min(
        100,
        ((countColors + 3) / colors.length) * 100
      ),
    },
    {
      id: 'controls',
      title: 'Controls',
      subtitle: 'Controls unlocked',
      icon: FaArrowsLeftRight,
      color: '#43F7FF',
      glow: 'rgba(67, 247, 255, 0.42)',
      value: countPedals + 1,
      total: pedals.length,
      progress: Math.min(
        100,
        ((countPedals + 1) / pedals.length) * 100
      ),
    },
    {
      id: 'tokens',
      title: 'Tokens',
      subtitle: 'Lifetime tokens collected',
      icon: MdToken,
      color: '#FF3157',
      glow: 'rgba(255, 49, 87, 0.42)',
      value: allTokens,
      total: 20000,
      progress: allTokensValue,
      displayValue: `${Math.min(allTokens, 20000).toLocaleString()} / 20K`,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />

      <ModalContent
        className="modal__simple modal__achievements"
        maxW={{ sm: '100%' }}
        mb="160px"
        overflow="hidden"
        position="relative"
      >
        <ModalCloseButton zIndex="20" />

        {/* Background neon */}
        <Box
          position="absolute"
          top="-100px"
          left="-90px"
          w="230px"
          h="230px"
          bg="rgba(0,224,255,0.08)"
          filter="blur(75px)"
          pointerEvents="none"
        />

        <Box
          position="absolute"
          top="-90px"
          right="-70px"
          w="220px"
          h="220px"
          bg="rgba(255,32,103,0.07)"
          filter="blur(75px)"
          pointerEvents="none"
        />

        {/* Header */}
        <ModalHeader
          px="20px"
          pt="20px"
          pb="14px"
          position="relative"
          bg="rgba(0,0,0,0.16)"
          borderBottom="1px solid rgba(255,255,255,0.04)"
        >
          <Text
            fontSize="21px"
            fontWeight="900"
            letterSpacing="0.4px"
            color="rgba(255,255,255,0.82)"
          >
            Profile
          </Text>

          <Flex
            align="center"
            gap="7px"
            mt="4px"
          >
            <Box
              w="6px"
              h="6px"
              borderRadius="50%"
              bg="#43F7FF"
              opacity="0.72"
              boxShadow="0 0 7px #43F7FF"
            />

            <Text
              fontSize="11px"
              color="rgba(255,255,255,0.38)"
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="0.8px"
            >
              Racing achievements & progress
            </Text>
          </Flex>
        </ModalHeader>

        <ModalBody
          px="14px"
          pt="14px"
          pb="18px"
          overflowY="auto"
          css={{
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(69,235,255,0.22)',
              borderRadius: '10px',
            },
          }}
        >
          {/* Main progress */}
          <Box
            position="relative"
            p="16px"
            mt="24px"
            borderRadius="18px"
            overflow="hidden"
            bg="linear-gradient(135deg, rgba(0,224,255,0.08), rgba(255,32,103,0.055))"
            border="1px solid rgba(67,247,255,0.18)"
            boxShadow="
              inset 0 0 24px rgba(67,247,255,0.025),
              0 0 24px rgba(0,0,0,0.18)
            "
          >
            <Box
              position="absolute"
              top="-50px"
              right="-30px"
              w="130px"
              h="130px"
              borderRadius="full"
              bg="rgba(255,216,77,0.08)"
              filter="blur(40px)"
              pointerEvents="none"
            />

            <Flex
              align="center"
              justify="space-between"
              gap="16px"
            >
              <Box flex="1" minW="0">
                <Flex
                  justify="space-between"
                  align="flex-end"
                  mb="9px"
                >
                  <Box>
                    <Text
                      fontSize="10px"
                      fontWeight="800"
                      textTransform="uppercase"
                      letterSpacing="1px"
                      color="rgba(255,255,255,0.38)"
                    >
                      Overall progress
                    </Text>

                    <Text
                      mt="2px"
                      fontSize="25px"
                      lineHeight="1"
                      fontWeight="950"
                      color="#F7FBFF"
                    >
                      {Math.round(progress)}%
                    </Text>
                  </Box>
                </Flex>

                <Box
                  position="relative"
                  w="100%"
                  h="6px"
                  borderRadius="20px"
                  bg="rgba(255,255,255,0.08)"
                  overflow="hidden"
                >
                  <Box
                    position="absolute"
                    left="0"
                    top="0"
                    h="100%"
                    w={`${progress}%`}
                    borderRadius="20px"
                    bg="linear-gradient(90deg, #43F7FF, #B447FF, #FF3157)"
                    boxShadow="0 0 10px rgba(67,247,255,0.45)"
                  />
                </Box>

                <Text
                  mt="9px"
                  maxW="220px"
                  fontSize="10px"
                  lineHeight="15px"
                  color="rgba(255,255,255,0.4)"
                >
                  Earn tokens and unlock new garage items
                  to increase your rank.
                </Text>

                <Flex
                  align="center"
                  gap="6px"
                  mt="11px"
                >
                  <MdToken
                    size={18}
                    color="#FF496A"
                    style={{
                      filter:
                        'drop-shadow(0 0 5px rgba(255,49,87,0.55))',
                    }}
                  />

                  <Text
                    fontSize="14px"
                    fontWeight="900"
                    color="#FF6680"
                  >
                    {tokens.toLocaleString()}
                  </Text>

                  <Text
                    fontSize="9px"
                    fontWeight="700"
                    color="rgba(255,255,255,0.3)"
                    textTransform="uppercase"
                    letterSpacing="0.7px"
                  >
                    balance
                  </Text>
                </Flex>
              </Box>

              {/* Rank */}
              <Box
                flexShrink="0"
                w="105px"
                textAlign="center"
              >
                <Box
                  position="relative"
                  w="88px"
                  h="88px"
                  mx="auto"
                  p="4px"
                  borderRadius="18px"
                  bg="linear-gradient(135deg, rgba(255,216,77,0.3), rgba(67,247,255,0.14))"
                  border="1px solid rgba(255,216,77,0.28)"
                  boxShadow="
                    0 0 20px rgba(255,184,0,0.12),
                    inset 0 0 14px rgba(255,255,255,0.04)
                  "
                >
                  <Image
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    borderRadius="14px"
                    src={objectsLVL?.image}
                  />

                  <Box
                    position="absolute"
                    bottom="-8px"
                    left="50%"
                    transform="translateX(-50%)"
                    px="9px"
                    py="3px"
                    borderRadius="10px"
                    bg="#12131A"
                    border="1px solid rgba(255,216,77,0.4)"
                    boxShadow="0 0 10px rgba(255,184,0,0.15)"
                    whiteSpace="nowrap"
                  >
                    <Text
                      fontSize="9px"
                      fontWeight="900"
                      color="#FFD84D"
                      textTransform="uppercase"
                      letterSpacing="0.6px"
                    >
                      Rank {objectsLVL?.rank}
                    </Text>
                  </Box>
                </Box>

                <Text
                  mt="14px"
                  fontSize="12px"
                  fontWeight="900"
                  color="#FFF3B0"
                  noOfLines={2}
                >
                  {objectsLVL?.name}
                </Text>
              </Box>
            </Flex>
          </Box>

          {/* Section header */}
          <Flex
            mt="18px"
            mb="9px"
            px="5px"
            justify="space-between"
            align="center"
          >
            <Text
              fontSize="11px"
              color="rgba(255,255,255,0.38)"
              fontWeight="800"
              letterSpacing="0.7px"
              textTransform="uppercase"
            >
              Achievements
            </Text>

            <Text
              fontSize="10px"
              color="rgba(255,255,255,0.25)"
            >
              Garage & racing
            </Text>
          </Flex>

          {/* Achievement cards */}
          <Flex
            direction="column"
            gap="8px"
          >
            {achievements.map((item) => {
              const Icon = item.icon;

              const completed =
                item.progress >= 100;

              return (
                <Flex
                  key={item.id}
                  position="relative"
                  align="center"
                  gap="12px"
                  minH="72px"
                  px="12px"
                  py="10px"
                  borderRadius="15px"
                  overflow="hidden"
                  bg="rgba(255,255,255,0.025)"
                  border={`1px solid ${
                    completed
                      ? item.color
                      : 'rgba(255,255,255,0.065)'
                  }`}
                  boxShadow={
                    completed
                      ? `0 0 18px ${item.glow}`
                      : 'none'
                  }
                >
                  {/* Icon */}
                  <Flex
                    flexShrink="0"
                    align="center"
                    justify="center"
                    w="43px"
                    h="43px"
                    borderRadius="13px"
                    bg={`${item.color}12`}
                    border={`1px solid ${item.color}35`}
                    color={item.color}
                    boxShadow={`inset 0 0 12px ${item.glow}`}
                  >
                    <Icon size={21} />
                  </Flex>

                  <Box
                    flex="1"
                    minW="0"
                  >
                    <Flex
                      align="center"
                      justify="space-between"
                      gap="8px"
                    >
                      <Box minW="0">
                        <Text
                          fontSize="13px"
                          fontWeight="900"
                          color="#F3F7FB"
                        >
                          {item.title}
                        </Text>

                        <Text
                          mt="1px"
                          fontSize="9px"
                          color="rgba(255,255,255,0.3)"
                          textTransform="uppercase"
                          letterSpacing="0.55px"
                        >
                          {item.subtitle}
                        </Text>
                      </Box>

                      <Text
                        flexShrink="0"
                        fontSize="11px"
                        fontWeight="900"
                        color={item.color}
                        fontVariantNumeric="tabular-nums"
                      >
                        {item.displayValue ||
                          `${item.value} / ${item.total}`}
                      </Text>
                    </Flex>

                    <Box
                      position="relative"
                      mt="9px"
                      w="100%"
                      h="5px"
                      borderRadius="10px"
                      overflow="hidden"
                      bg="rgba(255,255,255,0.07)"
                    >
                      <Box
                        position="absolute"
                        left="0"
                        top="0"
                        h="100%"
                        w={`${item.progress}%`}
                        borderRadius="10px"
                        bg={item.color}
                        boxShadow={`0 0 8px ${item.glow}`}
                      />
                    </Box>
                  </Box>

                  {completed && (
                    <Box
                      position="absolute"
                      top="6px"
                      right="7px"
                      px="5px"
                      py="2px"
                      borderRadius="5px"
                      bg={`${item.color}14`}
                    >
                      <Text
                        fontSize="7px"
                        lineHeight="1"
                        fontWeight="900"
                        color={item.color}
                        letterSpacing="0.6px"
                      >
                        MAX
                      </Text>
                    </Box>
                  )}
                </Flex>
              );
            })}
          </Flex>

          {/* Reset */}
          <Flex
            mt="18px"
            justify="center"
          >
            <Button
              onClick={ResetProgressOnOpen}
              variant="ghost"
              h="38px"
              px="14px"
              borderRadius="12px"
              bg="rgba(255,255,255,0.025)"
              border="1px solid rgba(255,255,255,0.06)"
              color="rgba(255,255,255,0.38)"
              _hover={{
                bg: 'rgba(255,49,87,0.07)',
                borderColor: 'rgba(255,49,87,0.15)',
                color: '#FF6680',
              }}
            >
              <FaArrowRotateRight size={13} />

              <Text
                ml="8px"
                fontSize="10px"
                fontWeight="800"
                textTransform="uppercase"
                letterSpacing="0.7px"
              >
                Reset Progress
              </Text>
            </Button>
          </Flex>

          <ResetProgressModal
            isOpen={ResetProgressIsOpen}
            onOpen={ResetProgressOnOpen}
            onClose={ResetProgressOnClose}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AchievementsModal;